<!-- eslint-disable vue/no-mutating-props -->
<template>
  <el-select
    v-model="model[prop]"
    v-bind="innerAttrs"
    :placeholder="innerAttrs.placeholder || '请选择'"
    :filterable="filterable"
    :collapseTags="collapseTags"
    :disabled="disabled"
    :multiple="multiple"
  >
    <template v-if="groupLabels?.length">
      <el-option-group v-for="label in groupLabels" :key="label" :label="label">
        <el-option
          v-for="(item, index) in getOptionsByGroupLabel(label)"
          :key="'select' + item[optionValueProp || 'value']"
          :label="item[optionLabelProp || 'label']"
          :value="item[optionValueProp || 'value']"
          :disabled="item.disabled"
        />
      </el-option-group>
    </template>
    <template v-else>
      <el-option
        v-for="(item, index) in selectOptions"
        :key="'select_' + item[optionValueProp || 'value']"
        :label="item[optionLabelProp || 'label']"
        :value="item[optionValueProp || 'value']"
        :disabled="item.disabled"
      />
    </template>
  </el-select>
</template>

<script lang="ts">
export default {
  inheritAttrs: false
}
</script>

<script setup lang="ts">
import { tpl, computed, ref, watch, useCurrentAppInstance } from '@zto/zpage'

import { useFormItem } from '../util'

import type { GenericFunction } from '@zto/zpage'
import { onMounted } from 'vue'

const props = withDefaults(
  defineProps<{
    model: Record<string, any>
    prop: string
    labelProp?: string // TODO: 返回label时使用，暂不支持
    disabled?: boolean
    options?: Array<any> | GenericFunction | string
    optionsLoader?: Function
    groupName?: string
    optionGroupProp?: string
    optionLabelProp?: string
    optionValueProp?: string
    collapseTags?: boolean
    filterable?: boolean
    optionsFilter?: GenericFunction
    noWriteback?: boolean
    multiple?: boolean
  }>(),
  {
    collapseTags: true,
    filterable: false,
    disabled: false,
    noWriteback: false,
    multiple: false
  }
)

const app = useCurrentAppInstance()

const { innerAttrs } = useFormItem(props, {
  clearModelEmptyPropOnChange: true
})

const innerContext = computed(() => {
  return app.useContext(props.model)
})

const groupLabels = ref<any[]>([])

const innerOptionsData = ref<any[]>(null)

const selectGroupName = computed(() => props.optionGroupProp || props.groupName)

const selectOptions = computed(() => {
  if (innerOptionsData.value) {
    return innerOptionsData.value
  }

  let optionsData: any = props.options || []
  if (typeof props.options === 'function') {
    optionsData = props.options(innerContext.value)
  } else if (typeof props.options === 'string') {
    optionsData = app.getCommonOptions(props.options) || []
  }

  return (optionsData || []) as any[]
})

watch(
  () => selectOptions.value,
  () => {
    resolveGroupLabels()
  },
  {
    immediate: true
  }
)

onMounted(async () => {
  await loadOptions()
})

async function loadOptions() {
  const context = app.useContext(props.model)

  if (props.optionsLoader) {
    const options = await props.optionsLoader(context)

    innerOptionsData.value = options || []
  }
}

/** 如果分组，计算分组信息 */
function resolveGroupLabels() {
  const groupNameVal = selectGroupName.value

  if (!groupNameVal) return

  const _groupLabels: any[] = []

  selectOptions.value.forEach((it: any) => {
    const groupLabel = it[groupNameVal]

    if (groupLabel && !_groupLabels.includes(groupLabel)) {
      _groupLabels.push(groupLabel)
    }
  })

  groupLabels.value = _groupLabels
}

/** 根据label获取options */
function getOptionsByGroupLabel(groupLabel: string) {
  const groupNameVal = selectGroupName.value
  if (!groupNameVal || !groupLabel) return

  const options: any[] = selectOptions.value.filter((it: any) => it[groupNameVal] === groupLabel)
  return options
}
</script>
